import { NgIf } from '@angular/common';
import { Component, DestroyRef, OnInit, ViewChild, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { PaButtonComponent } from 'src/app/shared/ui-kit/pa-button/pa-button.component';
import { PaInputComponent } from 'src/app/shared/ui-kit/pa-input/pa-input.component';
import { SupportLinkV2Component } from '../../components/support-link/support-link.component';
import { classifyPvzPincodeError } from '../../helpers/pvz-pincode-error.helper';
import { AuthActionsService } from '../../services/auth-actions.service';
import { AuthFlowService } from '../../services/auth-flow.service';
import { AuthSessionStore } from '../../services/auth-session.store';
import { LoginV2ApiService } from '../../services/login-v2-api.service';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-pvz-substitute-step',
  standalone: true,
  imports: [NgIf, ReactiveFormsModule, PaButtonComponent, PaInputComponent, SupportLinkV2Component],
  templateUrl: './pvz-substitute-step.component.html',
  styleUrls: ['../../styles/_step.styles.scss'],
})
export class PvzSubstituteStepComponent implements OnInit {
  readonly form = new FormGroup({
    login: new FormControl('', { nonNullable: true }),
    code: new FormControl('', { nonNullable: true }),
  });

  private readonly store = inject(AuthSessionStore);
  private readonly flow = inject(AuthFlowService);
  private readonly actions = inject(AuthActionsService);
  private readonly loginApi = inject(LoginV2ApiService);
  private readonly destroyRef = inject(DestroyRef);
  readonly environment = environment;
  private isSubmitting = false;
  pinErrorText = '';

  @ViewChild('substituteLoginInput') substituteLoginInput?: PaInputComponent;

  get showPinError(): boolean {
    return Boolean(this.pinErrorText);
  }

  ngOnInit(): void {
    this.form.controls.login.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((login) => this.store.patchAccount({ login: login ?? '' }));

    this.form.controls.code.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((code) => {
        if (code) {
          this.clearPinError();
        }
      });
  }

  back(): void {
    this.flow.goBack();
  }

  isSubmitDisabled(): boolean {
    const { login, code } = this.form.getRawValue();
    return (
      this.isSubmitting ||
      !login.trim() ||
      !/\d{4}/.test((code ?? '').toString().trim())
    );
  }

  async submit(): Promise<void> {
    if (this.isSubmitDisabled() || this.isSubmitting) {
      return;
    }

    this.isSubmitting = true;
    try {
      const { login, code } = this.form.getRawValue();
      const result = await this.actions.confirmSubstitute(login, code);
      if (!result || result.success) {
        return;
      }
      this.applyPincodeError(result.message, result.login_attempts);
    } finally {
      this.isSubmitting = false;
    }
  }

  private applyPincodeError(
    message?: string,
    loginAttempts?: number,
  ): void {
    const view = classifyPvzPincodeError(message, loginAttempts);
    this.form.controls.code.setValue('', { emitEvent: false });

    if (!view) {
      return;
    }

    this.loginApi.toast(view.message, 'error', 'Ошибка');
    this.pinErrorText = view.showHint ? view.message : '';
  }

  private clearPinError(): void {
    this.pinErrorText = '';
  }
}
